import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Plus, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { AgentListRow } from '@tasks/contracts';

import { listAgents } from '../../api/agents';
import { queryKeys } from '../../api/queryKeys';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, type BadgeTone } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Page, PageHeader } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { formatDateTime } from '../../lib/format';
import { agentRoleLabels } from '../../i18n/enums';
import { CreateSharedAgentModal } from './CreateSharedAgentModal';
import styles from './AgentsPages.module.css';

const STATUS_LABEL: Record<string, string> = {
  not_installed: 'Не установлен',
  queued: 'В очереди',
  installing: 'Установка…',
  installed: 'Установлен',
  failed: 'Ошибка',
  uninstalling: 'Удаление…',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  not_installed: 'neutral',
  queued: 'warning',
  installing: 'progress',
  installed: 'success',
  failed: 'danger',
  uninstalling: 'neutral',
};

function statusBadge(row: AgentListRow) {
  return <Badge tone={STATUS_TONE[row.installStatus] ?? 'neutral'}>{STATUS_LABEL[row.installStatus] ?? row.installStatus}</Badge>;
}

const COLUMNS: readonly TableColumn<AgentListRow>[] = [
  {
    id: 'agent',
    header: 'Агент',
    render: (agent) => (
      <>
        <div className={styles['name']}>
          {agent.displayName}
          {agent.isShared === true ? <Badge tone="progress">Общий</Badge> : null}
        </div>
        <div className={styles['muted']}>
          @{agent.login} · {agentRoleLabels[agent.agentRole as keyof typeof agentRoleLabels] ?? 'AI-агент'}
        </div>
      </>
    ),
  },
  {
    id: 'project',
    header: 'Проекты',
    render: (agent) => `Все проекты (${agent.projectCount ?? 0})`,
  },
  {
    id: 'server',
    header: 'Сервер',
    render: (agent) => <span className={styles['muted']}>{agent.agentServerHost ?? 'Не настроен'}</span>,
  },
  {
    id: 'llm',
    header: 'LLM',
    render: (agent) => agent.llmModel ?? <span className={styles['muted']}>Не настроен</span>,
  },
  { id: 'install', header: 'Установка', render: (agent) => statusBadge(agent) },
  {
    id: 'version',
    header: 'Версия',
    render: (agent) => <span className={styles['muted']}>{agent.installedVersion ?? '—'}</span>,
  },
  {
    id: 'actions',
    header: '',
    align: 'end',
    render: (agent) => (
      <Link to={`/agents/${agent.id}`}>
        <Button size="sm" variant="secondary" icon={<Settings2 size={14} />}>
          Настроить
        </Button>
      </Link>
    ),
  },
];

/** `/agents` — список AI-агентов и их установок (docs/16 §3.1). */
export function AgentsPage() {
  const { user } = useAuth();
  const [sharedRole, setSharedRole] = useState<'analyst' | 'developer' | null>(null);
  const isAdmin = user?.role === 'admin';

  const agentsQuery = useQuery({
    queryKey: queryKeys.agents.list,
    queryFn: ({ signal }) => listAgents(signal),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });

  if (agentsQuery.isPending) {
    return (
      <Page width="wide">
        <Skeleton variant="row" count={6} />
      </Page>
    );
  }

  if (agentsQuery.isError) {
    return (
      <Page width="wide">
        <EmptyState
          icon={<Bot size={20} />}
          title="Не удалось загрузить AI-агентов"
          description="Раздел доступен Admin и Project Admin. Обновите страницу или проверьте права."
        />
      </Page>
    );
  }

  const agents = [...agentsQuery.data.items].sort(
    (left, right) => Number(right.isShared === true) - Number(left.isShared === true),
  );
  const hasSharedAnalyst = agents.some(
    (agent) => agent.isShared === true && agent.agentRole === 'analyst',
  );
  const hasSharedDeveloper = agents.some(
    (agent) => agent.isShared === true && agent.agentRole === 'developer',
  );

  return (
    <Page width="wide">
      <PageHeader
        title="AI-агенты"
        description="Общие AI-агенты обслуживают несколько проектов; у каждого агента отдельный VPS."
        actions={
          isAdmin ? (
            <>
              <Link to="/admin/users">
                <Button variant="ghost">К пользователям</Button>
              </Link>
              {hasSharedAnalyst ? null : (
                <Button
                  variant="primary"
                  icon={<Plus size={14} />}
                  onClick={() => setSharedRole('analyst')}
                >
                  Создать AI-аналитика
                </Button>
              )}
              {hasSharedDeveloper ? null : (
                <Button
                  variant={hasSharedAnalyst ? 'primary' : 'ghost'}
                  icon={<Plus size={14} />}
                  onClick={() => setSharedRole('developer')}
                >
                  Создать AI-разработчика
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      {agents.length === 0 ? (
        <EmptyState
          icon={<Bot size={20} />}
          title="AI-агентов пока нет"
          description={
            isAdmin
              ? 'Создайте AI-аналитика или AI-разработчика.'
              : 'AI-агентов создаёт администратор.'
          }
        />
      ) : (
        <Table
          ariaLabel="AI-агенты"
          columns={COLUMNS}
          rows={agents}
          getRowId={(agent) => agent.id}
        />
      )}

      {isAdmin && sharedRole !== null ? (
        <CreateSharedAgentModal
          open
          role={sharedRole}
          onClose={() => setSharedRole(null)}
        />
      ) : null}

      <p className={styles['muted']}>
        {agents.length > 0
          ? `Обновлено: ${formatDateTime(new Date().toISOString())}`
          : 'После установки агент появится в списке исполнителей проекта.'}
      </p>
    </Page>
  );
}
